<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <router-link to="/client/inventory/transfer-orders" class="back-btn">
          <i class="ri-arrow-left-line"></i>
        </router-link>
        <h1 class="page-title">新增調撥單</h1>
      </div>
      <div class="header-actions">
        <router-link to="/client/inventory/transfer-orders" class="btn btn-secondary">取消</router-link>
        <button type="button" class="btn btn-primary" :disabled="saving" @click="submitForm">
          {{ saving ? '儲存中...' : '建立調撥單' }}
        </button>
      </div>
    </div>

    <div class="page-container">

    <div class="form-card">
      <h3 class="section-title">調撥資訊</h3>
      <div class="form-row">
        <div class="form-group">
          <label class="form-label required">出貨倉庫（總倉）</label>
          <select v-model="form.fromWarehouseId" @change="onFromWarehouseChange" class="form-control">
            <option value="">請選擇</option>
            <option v-for="w in warehouses" :key="w.id" :value="w.id">
              {{ w.warehouseName }}（{{ w.warehouseType === 'main' ? '總倉' : '分倉' }}）
            </option>
          </select>
        </div>
        <div class="form-group">
          <label class="form-label required">目標倉庫（分倉）</label>
          <select v-model="form.toWarehouseId" class="form-control">
            <option value="">請選擇</option>
            <option v-for="w in targetWarehouses" :key="w.id" :value="w.id">{{ w.warehouseName }}</option>
          </select>
        </div>
      </div>
      <div class="form-group">
        <label class="form-label">備註</label>
        <textarea v-model="form.notes" class="form-control" rows="2"></textarea>
      </div>
    </div>

    <div class="form-card">
      <div class="section-header">
        <h3 class="section-title">調撥明細</h3>
        <button class="btn btn-outline btn-sm" @click="openProductSearch" :disabled="!form.fromWarehouseId">
          <i class="ri-add-line"></i> 新增商品
        </button>
      </div>
      <p v-if="!form.fromWarehouseId" class="tip-text">請先選擇出貨倉庫，才能查詢可用庫存</p>

      <div class="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>商品名稱</th>
              <th>規格 SKU</th>
              <th>申請調撥數量</th>
              <th>可用庫存</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!form.items.length">
              <td colspan="5" class="text-center text-muted">請新增調撥明細</td>
            </tr>
            <tr v-for="(item, idx) in form.items" :key="idx">
              <td>{{ item.productName }}</td>
              <td><code>{{ item.variantSku }}</code></td>
              <td>
                <input v-model.number="item.requestedQuantity" type="number" :min="1" :max="item.availableQuantity" class="form-control input-sm" />
              </td>
              <td>
                <span :class="item.availableQuantity <= 0 ? 'text-danger' : ''">{{ item.availableQuantity }}</span>
              </td>
              <td>
                <button class="btn btn-sm btn-danger-text" @click="removeItem(idx)">
                  <i class="ri-delete-bin-line"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- 共用倉庫庫存挑選 Modal -->
    <WarehouseInventoryPickerModal
      :open="showProductSearch"
      :warehouse-id="form.fromWarehouseId"
      title="選擇調撥商品"
      @close="showProductSearch = false"
      @select="addItemFromInventory"
    />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { transferOrderService, warehouseService } from '@/api/modules/warehouses.js';
import WarehouseInventoryPickerModal from '@/components/WarehouseInventoryPickerModal.vue';

const router = useRouter();
const saving = ref(false);
const warehouses = ref([]);
const showProductSearch = ref(false);

const form = ref({ fromWarehouseId: '', toWarehouseId: '', notes: '', items: [] });

const targetWarehouses = computed(() =>
  warehouses.value.filter(w => w.id !== form.value.fromWarehouseId)
);

async function fetchWarehouses() {
  try {
    const res = await warehouseService.getWarehouses({ status: 'active' });
    warehouses.value = res.data?.items || [];
  } catch (e) { console.error(e); }
}

function onFromWarehouseChange() {
  form.value.toWarehouseId = '';
  form.value.items = [];
}

function openProductSearch() {
  showProductSearch.value = true;
}

function addItemFromInventory(inv) {
  const exists = form.value.items.find(i => i.variantId === inv.variantId);
  if (exists) { alert('該規格已在明細中'); return; }
  form.value.items.push({
    variantId: inv.variantId,
    productName: inv.productName,
    variantSku: inv.variantSku,
    availableQuantity: inv.availableQuantity,
    requestedQuantity: 1
  });
}

function removeItem(idx) { form.value.items.splice(idx, 1); }

async function submitForm() {
  if (!form.value.fromWarehouseId) { alert('請選擇出貨倉庫'); return; }
  if (!form.value.toWarehouseId) { alert('請選擇目標倉庫'); return; }
  if (!form.value.items.length) { alert('請新增至少一筆明細'); return; }
  for (const item of form.value.items) {
    if (item.requestedQuantity < 1) { alert('調撥數量必須大於 0'); return; }
    if (item.requestedQuantity > item.availableQuantity) {
      alert(`「${item.productName} ${item.variantSku}」調撥數量超過可用庫存`); return;
    }
  }

  saving.value = true;
  try {
    const payload = {
      fromWarehouseId: form.value.fromWarehouseId,
      toWarehouseId: form.value.toWarehouseId,
      notes: form.value.notes || undefined,
      items: form.value.items.map(i => ({ variantId: i.variantId, requestedQuantity: i.requestedQuantity }))
    };
    const res = await transferOrderService.createTransferOrder(payload);
    const id = res.data?.id;
    router.push(id ? `/client/inventory/transfer-orders/${id}` : '/client/inventory/transfer-orders');
  } catch (e) {
    alert(e.response?.data?.message || '建立失敗');
  } finally { saving.value = false; }
}

onMounted(fetchWarehouses);
</script>

<style scoped>
.page-container { padding: 20px; max-width: 900px; }
.page-header { display: flex; align-items: center; margin-bottom: 20px; }
.page-header-left { display: flex; align-items: center; gap: 10px; }
.back-btn { color: #666; font-size: 20px; text-decoration: none; }
.page-title { font-size: 22px; font-weight: 600; margin: 0; }
.form-card { background: #fff; border: 1px solid #e8e8e8; border-radius: 10px; padding: 20px; margin-bottom: 16px; }
.section-title { font-size: 15px; font-weight: 600; margin: 0; color: #333; }
.section-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.form-group { margin-bottom: 14px; }
.form-label { display: block; font-size: 13px; font-weight: 500; margin-bottom: 4px; color: #555; }
.form-label.required::after { content: ' *'; color: #e74c3c; }
.form-control { width: 100%; padding: 8px 10px; border: 1px solid #ddd; border-radius: 6px; font-size: 14px; box-sizing: border-box; }
.input-sm { width: 80px; padding: 4px 8px; }
.table-wrapper { overflow-x: auto; }
table { width: 100%; border-collapse: collapse; }
th, td { padding: 10px 12px; text-align: left; border-bottom: 1px solid #f0f0f0; font-size: 14px; }
th { background: #f8f9fa; font-weight: 600; color: #555; }
.tip-text { color: #999; font-size: 13px; margin: 0 0 12px; }
.text-center { text-align: center; }
.text-muted { color: #999; }
.text-danger { color: #e74c3c; }
.text-success { color: #27ae60; }
.form-actions { display: flex; justify-content: flex-end; gap: 12px; margin-top: 20px; }
.btn { padding: 8px 20px; border-radius: 6px; cursor: pointer; font-size: 14px; border: none; text-decoration: none; display: inline-block; }
.btn-primary { background: #1a73e8; color: #fff; }
.btn-secondary { background: #f0f0f0; color: #333; }
.btn-outline { background: transparent; border: 1px solid #1a73e8; color: #1a73e8; }
.btn-sm { padding: 4px 10px; font-size: 12px; border-radius: 4px; }
.btn-danger-text { background: none; border: none; color: #e74c3c; font-size: 16px; cursor: pointer; }
.btn:disabled { opacity: .6; cursor: not-allowed; }
/* picker modal 樣式由 WarehouseInventoryPickerModal 元件提供 */
</style>
